import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Safe environment variable access
export function getEnvVar(key: string, defaultValue?: string): string | undefined {
  const value = process.env[key]
  if (value === undefined && defaultValue === undefined) {
    console.warn(`Environment variable ${key} is not set`)
  }
  return value || defaultValue
}

// Validate required environment variables
export function validateEnvVars(required: string[]): void {
  const missing: string[] = []
  
  for (const key of required) {
    if (!process.env[key]) {
      missing.push(key)
    }
  }
  
  if (missing && missing.length > 0) {
    console.warn(`Missing environment variables: ${missing.join(', ')}`)
  }
}

// Safe array access with fallback
export function safeArrayAccess<T>(array: T[] | undefined | null, index: number, fallback?: T): T | undefined {
  if (!array || !Array.isArray(array)) {
    return fallback
  }
  return array[index] !== undefined ? array[index] : fallback
}

// Safe object property access
export function safePropertyAccess<T>(obj: any, path: string, fallback?: T): T | undefined {
  if (!obj || typeof obj !== 'object') {
    return fallback
  }
  
  const keys = path.split('.')
  let current = obj
  
  for (const key of keys) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return fallback
    }
    current = current[key]
  }
  
  return current !== undefined ? current : fallback
}

// Format currency
export function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount)
}

// Format date
export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  }).format(dateObj)
}

// Generate random ID
export function generateId(length: number = 8): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

// Debounce function
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

// Throttle function
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}
